import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CpuIcon, TrophyIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { fetchTechRecap } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { InfoTip } from "./InfoTip";

const CYAN = "#22D3EE";

const multColor = (m) => (!m ? "#888" : m >= 3 ? CYAN : m >= 1.5 ? "#4ADE80" : "#888");

const RecapRow = ({ r }) => (
  <Link
    to={`/coin/${r.mint}`}
    data-testid={`tech-recap-row-${r.mint}`}
    className="flex items-center gap-2 px-3 py-2 border border-white/10 hover:bg-white/[0.04] transition-colors"
  >
    <span className="font-heading font-bold text-sm truncate flex-1 min-w-0">${r.symbol || r.mint.slice(0, 4)}</span>
    {r.keyword && (
      <span className="hidden sm:inline text-[9px] font-mono px-1.5 py-0.5 shrink-0" style={{ background: `${CYAN}18`, border: `1px solid ${CYAN}55`, color: CYAN }}>🔬 {r.keyword}</span>
    )}
    <span className="font-mono text-[11px] text-muted-foreground shrink-0 hidden xs:inline">{fmtUsd(r.calledMcap)}→{fmtUsd(r.peakMcap)}</span>
    <span className="font-heading font-black text-sm shrink-0 w-14 text-right" style={{ color: multColor(r.peakMultiple) }}>
      {r.peakMultiple ? `${r.peakMultiple}×` : "—"}
    </span>
  </Link>
);

const Column = ({ title, icon: Icon, rows, testid, empty }) => (
  <div className="space-y-2" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon size={16} weight="fill" style={{ color: CYAN }} />
      <h3 className="font-heading font-bold text-sm tracking-tight">{title}</h3>
    </div>
    {rows.length === 0 ? (
      <div className="border border-white/10 bg-card py-6 text-center text-muted-foreground font-mono text-xs px-4">{empty}</div>
    ) : (
      <div className="flex flex-col gap-1.5">{rows.map((r) => <RecapRow key={r.mint} r={r} />)}</div>
    )}
  </div>
);

export const TechRecap = () => {
  const { data } = useQuery({ queryKey: ["techRecap"], queryFn: fetchTechRecap, refetchInterval: 180000, placeholderData: (p) => p });
  const today = data?.today || [];
  const yesterday = data?.yesterday || [];
  const best = data?.bestYesterday;

  if (today.length === 0 && yesterday.length === 0) return null;

  return (
    <div className="space-y-4 border-t border-white/10 pt-8" data-testid="tech-recap">
      <div className="flex items-center gap-2.5">
        <CpuIcon size={22} weight="fill" style={{ color: CYAN }} />
        <h2 className="font-heading font-black text-xl sm:text-2xl tracking-tighter leading-none">
          TECH TOKENS <span style={{ color: CYAN }}>RECAP</span>
        </h2>
        <InfoTip
          term="techScore"
          title="Tech Tokens Recap"
          desc="How the tech tokens we flagged actually performed — the peak multiple they reached from the market cap we first called them at. Proof the Tech Score is finding real product plays."
          color={CYAN}
        >
          <span className="text-muted-foreground text-xs border border-white/15 rounded-full w-4 h-4 flex items-center justify-center font-mono">i</span>
        </InfoTip>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        How the tech tokens we flagged actually ran — peak multiple from the market cap we first called them at.
      </p>
      {best && (
        <div className="flex items-center gap-2 px-4 py-2.5 border" style={{ borderColor: `${CYAN}55`, background: `${CYAN}0D` }} data-testid="tech-recap-best">
          <TrophyIcon size={18} weight="fill" style={{ color: CYAN }} />
          <span className="text-sm">
            <span className="text-muted-foreground">Best tech call yesterday: </span>
            <Link to={`/coin/${best.mint}`} className="font-heading font-bold hover:text-[#22D3EE] transition-colors">${best.symbol || best.mint.slice(0, 4)}</Link>
            <span className="font-heading font-black ml-1.5" style={{ color: CYAN }}>{best.peakMultiple ? `${best.peakMultiple}×` : ""}</span>
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Column title="Today" icon={ClockCounterClockwiseIcon} rows={today} testid="tech-recap-today" empty="No tech calls yet today — check back soon." />
        <Column title="Yesterday" icon={ClockCounterClockwiseIcon} rows={yesterday} testid="tech-recap-yesterday" empty="No tech calls logged yesterday." />
      </div>
    </div>
  );
};
